import excel from 'exceljs';
import moment from 'moment';
import { getRoundedNumber } from '../../ui/utils/common';
import { decryptString } from '../../ui/utils';
import { convertLocalDate } from '../../ui/utils/helper';
import { DynamicObjectInterface } from '../type';
import { axiosPost } from './axiosUtils';

const generateReport = {
  async leaderListReport(name: string, campaignDetail: DynamicObjectInterface) {
    const workbook = new excel.Workbook();

    const worksheet = workbook.addWorksheet('Leader List');

    worksheet.mergeCells('A1', 'C1');
    worksheet.getCell('A1').value = 'Report - Leader List';
    worksheet.getCell('A1').font = { bold: true };

    worksheet.getCell('A2').value = 'Campaign';
    worksheet.getCell('A2').font = { bold: true };

    worksheet.mergeCells('B2', 'F2');
    worksheet.getCell('B2').value = `${campaignDetail.campaignId} - ${campaignDetail.campaignName}`;
    worksheet.getCell('B2').font = { color: { argb: 'FF0000' } };

    worksheet.mergeCells('B3', 'D3');
    worksheet.getCell('A3').value = 'Period';
    worksheet.getCell('A3').font = { bold: true };
    worksheet.getCell('B3').value = `${moment(campaignDetail.campaignStart).format('DD MMM YYYY')} ~ ${moment(
      campaignDetail.campaignEnd,
    ).format('DD MMM YYYY')}`;

    // Column headers
    worksheet.getRow(4).values = [
      'No.',
      'Leader ABO ID',
      'Leader Name',
      'Leader Pin',
      'Total Coach',
      'Total Qualified Coach',
      'Total Point',
      'New Personal Sponsored ABO/APC',
      'Group Sponsored New ABOs',
      'Rewards',
    ];
    worksheet.columns = [
      { key: 'no' },
      { key: 'leaderAboId' },
      { key: 'leaderName' },
      { key: 'leaderPin' },
      { key: 'totalCoach' },
      { key: 'totalQualifiedCoach' },
      { key: 'totalPoint' },
      { key: 'personalSponsored' },
      { key: 'groupSponsored' },
      { key: 'rewards' },
    ];
    worksheet.getRow(4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FCE4D6' } };
    worksheet.getRow(4).alignment = { vertical: 'bottom', horizontal: 'center' };

    let result = await axiosPost(`report/?name=${name}`, { campaignId: campaignDetail.campaignId });

    if (result.status === 200) {
      result.data.data.forEach(function (item: DynamicObjectInterface, index: string) {
        worksheet.addRow({
          no: index + 1,
          leaderAboId: item.aboId,
          leaderName: item.name,
          leaderPin: item.pin,
          totalCoach: item.totalCoach,
          totalQualifiedCoach: item.totalQualifiedCoach.value + item.totalQualifiedCoach.valueAdj,
          totalPoint: item.totalPoint.value + item.totalPoint.valueAdj,
          personalSponsored: item.totalAboReqruitment.value + item.totalAboReqruitment.valueAdj,
          groupSponsored: item.newGroupAboCount.value + item.newGroupAboCount.valueAdj,
          rewards: item.rewards
            .map((reward: DynamicObjectInterface) =>
              `${reward.rewardName} ${
                reward.redeemDate ? '(' + moment(reward.redeemDate).format('DD MMM YYYY') + ')' : ''
              }`.trim(),
            )
            .join(', '),
        });
      });
    }

    return workbook;
  },
  async teamListReport(name: string, campaignDetail: DynamicObjectInterface) {
    const workbook = new excel.Workbook();

    const worksheet = workbook.addWorksheet('Report - Team');
    let maxRewardsLength = 0;

    worksheet.getCell('A1').value = `Report - Team`;
    worksheet.getCell('A2').value = `Campaign`;
    worksheet.getCell('B2').value = `${campaignDetail.campaignId} - ${campaignDetail.campaignName}`;
    worksheet.getCell('B2').font = { color: { argb: 'FF0000' } };

    worksheet.getCell('A1').font = { bold: true };
    worksheet.getCell('A2').font = { bold: true };

    // Column headers
    let headers = [
      'Campaign ID',
      'Team Name',
      'Team Description',
      'Team Start Date DD-MM-YYYY',
      'Team End Date DD-MM-YYYY',
      'Team Status',
      'Coach ID',
      'Is Individual Team',
      'Current Team Size',
      //'Is Team Leader',
      'ABO ID',
      'Name',
      'Contact',
      'Email',
      'Address',
      'Race',
      'Gender',
      'Current Pin Level',
      'Highest Pin Achieved',
      'BA/LOA Name',
      'Member Type',
      'New ABO',
      'Occupation',
      'Income Range per Month',
      //'Invisible',
      'Remark',
      'Participant Status',
      'Order Number',
      'Product Name',
      'Order Qualified',
      //'Point',
      //'Adjusted Point',
      'SOP Order Number',
      'Order Qualified',
      // 'Point',
      // 'Adjusted Point',
      'Date',
      'Machine',
      'Height',
      'Weight (KG)',
      'Muscle Mass (KG)',
      'Muscle Mass (%)',
      'Body Fat (%)',
      'Acknowledged By',
      'Acknowledged Date',
      'Photo - Front',
      'Photo - Back',
      'Date',
      'Machine',
      'Height',
      'Weight (KG)',
      'Muscle Mass (KG)',
      'Muscle Mass (%)',
      'Body Fat (%)',
      'Acknowledged By',
      'Acknowledged Date',
      'Photo - Front',
      'Photo - Back',
      // 'Team Weight diff (%)',
      // 'Participant Weight diff (kg)',
      // 'Team Body Fat diff (%)',
      'Participant Body Fat diff (%)',
      // 'Team Muscle Mass diff (%)',
      'Participant Muscle Mass diff (kg)',
      'Participant Muscle Mass diff (%)',
      // 'Body Weight',
      // 'Body Fat',
      // 'Muscle Mass',
      // 'Group Winning Prize',
      // 'Point',
      // 'Adjusted Point',
      'Completed Journey',
      'Achieved Individual Criteria',
    ];
    let columns = [
      { key: 'campaignId' },
      { key: 'teamName' },
      { key: 'teamDescription' },
      { key: 'teamStartDate' },
      { key: 'teamEndDate' },
      { key: 'teamStatus' },
      { key: 'coachId' },
      { key: 'isIndividualTeam' },
      { key: 'currentTeamSize' },
      //  { key: 'isTeamLeader' },
      { key: 'aboId' },
      { key: 'name' },
      { key: 'phone' },
      { key: 'email' },
      { key: 'address' },
      { key: 'race' },
      { key: 'gender' },
      { key: 'currentPinLevel' },
      { key: 'highestPinAchieved' },
      { key: 'baLoaName' },
      { key: 'memberType' },
      { key: 'isNewAbo' },
      { key: 'occupation' },
      { key: 'incomeRangePerMonth' },
      // { key: 'isInvisible' },
      { key: 'remark' },
      { key: 'enrollmentStatus' },
      { key: 'orderNumber' },
      { key: 'productName' },
      { key: 'orderQualified' },
      // { key: 'orderPoint' },
      // { key: 'orderAdjustedPoint' },
      { key: 'alpOrderNumber' },
      { key: 'alpOrderQualified' },
      // { key: 'alpPoint' },
      // { key: 'alpAdjustedPoint' },
      { key: 'weightInDate' },
      { key: 'weightInMachine' },
      { key: 'weightInHeight' },
      { key: 'weightInWeight' },
      { key: 'weightInMuscleMass' },
      { key: 'weightInMusclePercentage' },
      { key: 'weightInBodyFat' },
      { key: 'weightInAcknowledgeBy' },
      { key: 'weightInAcknowledgeDate' },
      { key: 'weightInPhotoFront' },
      { key: 'weightInPhotoBack' },
      { key: 'weightOutDate' },
      { key: 'weightOutMachine' },
      { key: 'weightOutHeight' },
      { key: 'weightOutWeight' },
      { key: 'weightOutMuscleMass' },
      { key: 'weightOutMusclePercentage' },
      { key: 'weightOutBodyFat' },
      { key: 'weightOutAcknowledgeBy' },
      { key: 'weightOutAcknowledgeDate' },
      { key: 'weightOutPhotoFront' },
      { key: 'weightOutPhotoBack' },
      // { key: 'teamWeightDiff' },
      // { key: 'participantWeightDiff' },
      // { key: 'teamBodyFatDiff' },
      { key: 'participantBodyFatDiff' },
      // { key: 'teamMuscleMassDiff' },
      { key: 'participantMuscleMassDiff' },
      { key: 'participantMusclePercentageDiff' },
      // { key: 'winBodyWeight' },
      // { key: 'winBodyFat' },
      // { key: 'winMuscleMass' },
      // { key: 'groupWinningPrize' },
      // { key: 'coachPoint' },
      // { key: 'coachAdjustedPoint' },
      { key: 'completedJourney' },
      { key: 'achievedIndividualCriteria' },
    ];

    let res = await axiosPost(`report/?name=${name}`, { campaignId: campaignDetail.campaignId });
    const { results: result, transactionResults, rewardResults, criteria } = res?.data?.data ?? [];

    result.sort((a: DynamicObjectInterface, b: DynamicObjectInterface) => {
      const dateComparison =
        (new Date(a.teamStartDate) as Date).getTime() - (new Date(b.teamStartDate) as Date).getTime();
      if (dateComparison !== 0) {
        return dateComparison;
      }
      return a.teamName.localeCompare(b.teamName);
    });

    let mappedResult = [];
    for (let i = 0; i < result.length; i++) {
      const item = result[i];
      const userId = item.userId;
      const teamId = item.teamId;
      const weightIn = transactionResults?.find(
        (i: DynamicObjectInterface) => i.loginId === userId && i.teamId === teamId && i.action === 'WEIGHT_IN',
      );
      const weightOut = transactionResults?.find(
        (i: DynamicObjectInterface) => i.loginId === userId && i.teamId === teamId && i.action === 'WEIGHT_OUT',
      );
      const purchase = transactionResults?.find(
        (i: DynamicObjectInterface) => i.loginId === userId && i.teamId === teamId && i.action === 'PURCHASE',
      );
      const subscribe = transactionResults?.find(
        (i: DynamicObjectInterface) => i.loginId === userId && i.teamId === teamId && i.action === 'SUBSCRIBE',
      );
      const { point: purchasePoint, adjustedPoint: purchaseAdjPoint } =
        rewardResults?.find(
          (i: DynamicObjectInterface) =>
            i.loginId === userId && i.teamId === teamId && i.kpiCode === 'PURCHASE_STARTKIT',
        ) ?? [];
      const { point: subscribePoint, adjustedPoint: subscribeAdjPoint } =
        rewardResults?.find(
          (i: DynamicObjectInterface) => i.loginId === userId && i.teamId === teamId && i.kpiCode === 'SUBSCRIBE_ALP',
        ) ?? [];
      const { point: winningPoint, adjustedPoint: winningAdjPoint } =
        rewardResults?.find(
          (i: DynamicObjectInterface) =>
            i.loginId === userId && i.teamId === teamId && i.kpiCode === 'WEIGHT_ACHIEVEMENT',
        ) ?? [];

      const rewards = rewardResults
        ?.filter((i: DynamicObjectInterface) => i.loginId === userId && i.teamId === teamId && i.rewardName)
        ?.map(
          (reward: DynamicObjectInterface) =>
            `${reward.rewardName} ${reward.redeemDate ? `(${moment(reward.redeemDate).format('DD-MM-YYYY')})` : ''}`,
        );

      if (rewards != null && rewards && rewards.length > maxRewardsLength) {
        maxRewardsLength = rewards.length;
      }

      if (!mappedResult[teamId]) {
        mappedResult[teamId] = {
          teamWeightDiff: 0,
          teamBodyFatDiff: 0,
          teamMuscleMassDiff: 0,
          [userId]: {
            weightIn,
            weightOut,
            purchase,
            subscribe,
            weightOutDiffValues: {
              weightDiff: 0,
              bodyFatDiff: 0,
              muscleKilogramDiff: 0,
              musclePercentageDiff: 0,
              weightPercentageDiff: 0,
              bodyFatPercentageDiff: 0,
            },
            purchasePoint,
            purchaseAdjPoint,
            subscribePoint,
            subscribeAdjPoint,
            winningPoint,
            winningAdjPoint,
            rewards,
          },
        };
      } else {
        mappedResult[teamId][userId] = {
          weightIn,
          weightOut,
          purchase,
          subscribe,
          weightOutDiffValues: {
            weightDiff: 0,
            bodyFatDiff: 0,
            muscleKilogramDiff: 0,
            musclePercentageDiff: 0,
            weightPercentageDiff: 0,
            bodyFatPercentageDiff: 0,
          },
          purchasePoint,
          purchaseAdjPoint,
          subscribePoint,
          subscribeAdjPoint,
          winningPoint,
          winningAdjPoint,
          rewards,
        };
      }

      if (
        weightOut?.weight &&
        weightIn?.weight &&
        weightOut?.bodyFat &&
        weightIn?.bodyFat &&
        weightOut?.muscleKilogram &&
        weightIn?.muscleKilogram
      ) {
        mappedResult[teamId][userId].weightOutDiffValues = {
          weightDiff: getRoundedNumber(
            getRoundedNumber(parseFloat(decryptString(weightOut.weight)) || 0) -
              getRoundedNumber(parseFloat(decryptString(weightIn.weight)) || 0),
          ),
          bodyFatDiff: getRoundedNumber(
            getRoundedNumber(parseFloat(decryptString(weightOut.bodyFat)) || 0) -
              getRoundedNumber(parseFloat(decryptString(weightIn.bodyFat)) || 0),
          ),
          muscleKilogramDiff: getRoundedNumber(
            getRoundedNumber(parseFloat(decryptString(weightOut.muscleKilogram)) || 0) -
              getRoundedNumber(parseFloat(decryptString(weightIn.muscleKilogram)) || 0),
          ),
          musclePercentageDiff: getRoundedNumber(
            (getRoundedNumber(parseFloat(decryptString(weightOut.muscleKilogram)) || 0) /
              getRoundedNumber(parseFloat(decryptString(weightOut.weight)) || 0)) *
              100 -
              (getRoundedNumber(parseFloat(decryptString(weightIn.muscleKilogram)) || 0) /
                getRoundedNumber(parseFloat(decryptString(weightIn.weight)) || 0)) *
                100,
          ),
          weightPercentageDiff: getRoundedNumber(
            ((getRoundedNumber(parseFloat(decryptString(weightOut.weight)) || 0) -
              (getRoundedNumber(parseFloat(decryptString(weightIn.weight))) || 0)) /
              getRoundedNumber(parseFloat(decryptString(weightIn.weight)) || 0)) *
              100,
          ),
          bodyFatPercentageDiff: getRoundedNumber(
            getRoundedNumber(parseFloat(decryptString(weightOut.bodyFat)) || 0) -
              getRoundedNumber(parseFloat(decryptString(weightIn.bodyFat)) || 0),
          ),
        };

        if (result?.[i]?.enrollmentStatus === 'ACTIVE') {
          mappedResult[teamId].teamWeightDiff +=
            mappedResult[teamId][userId].weightOutDiffValues.weightPercentageDiff ?? 0;
          mappedResult[teamId].teamBodyFatDiff += mappedResult[teamId][userId]?.weightOutDiffValues?.bodyFatDiff ?? 0;
          mappedResult[teamId].teamMuscleMassDiff +=
            mappedResult[teamId][userId]?.weightOutDiffValues?.muscleKilogramDiff ?? 0;
        }
      }
    }

    let teamCriteria = [];

    for (let i = 0; i < result?.length; i++) {
      const item = result[i];
      const userId = item.userId;
      const teamId = item.teamId;
      const transactionItem = mappedResult[teamId][userId];

      if (!teamCriteria[teamId]) {
        teamCriteria[teamId] = {
          result: true,
        };
      }

      const hitCriteria =
        (-transactionItem.weightOutDiffValues?.weightPercentageDiff >= parseFloat(criteria.indiWeightToLoss) &&
          -mappedResult[teamId].teamWeightDiff >= parseFloat(criteria.teamWeightToLossTeam)) ||
        (-transactionItem.weightOutDiffValues?.bodyFatDiff >= parseFloat(criteria.indiBodyFatToLossIndi) &&
          -mappedResult[teamId].teamBodyFatDiff >= parseFloat(criteria.teamBodyFatToLossTeam)) ||
        (transactionItem.weightOutDiffValues?.muscleKilogramDiff >= parseFloat(criteria.indiMuscleToGainIndi) &&
          mappedResult[teamId].teamMuscleMassDiff >= parseFloat(criteria.teamMuscleToGainTeam));

      if (!hitCriteria) teamCriteria[teamId].result = false;
    }

    // update column number here
    if (maxRewardsLength > 0) worksheet.mergeCells(3, 58, 3, 58 + (maxRewardsLength - 1));
    const rowRewards = worksheet.getRow(3);
    rowRewards.getCell(58).value = 'Rewards';
    rowRewards.getCell(58).alignment = { horizontal: 'center', vertical: 'middle' };
    rowRewards.getCell(58).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'BF8F00' },
    };

    for (let i = 1; i <= maxRewardsLength; i++) {
      headers.push('Reward ' + i.toString());
      columns.push({ key: 'reward' + i });
    }

    if (maxRewardsLength === 0) {
      headers.push('Reward ' + 1);
      columns.push({ key: 'reward' + 1 });
    }

    worksheet.getRow(4).values = headers;
    worksheet.columns = columns;

    for (let i = 58; i <= 58 + (maxRewardsLength - 1); i++) {
      const rowRewardsChildren = worksheet.getRow(4);
      rowRewardsChildren.getCell(i).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'BF8F00' },
      };
    }

    if (maxRewardsLength === 0) {
      worksheet.getCell('BE4').fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'BF8F00' },
      };
    }

    // //region Empty
    // for (let colCode = 'A'.charCodeAt(0); colCode < 'Y'.charCodeAt(0); colCode++) {
    //   const col = String.fromCharCode(colCode);
    //   worksheet.getCell(col + '4').fill = {
    //     type: 'pattern',
    //     pattern: 'solid',
    //     fgColor: { argb: 'E2EFDA' },
    //   };
    // }

    // for (let colCode = 'Y'.charCodeAt(0); colCode <= 'Z'.charCodeAt(0); colCode++) {
    //   const col = String.fromCharCode(colCode);
    //   worksheet.getCell(col + '4').fill = {
    //     type: 'pattern',
    //     pattern: 'solid',
    //     fgColor: { argb: 'D9E1F2' },
    //   };
    // }
    // worksheet.getCell('AA4').fill = {
    //   type: 'pattern',
    //   pattern: 'solid',
    //   fgColor: { argb: 'D9E1F2' },
    // };

    // //region A
    // for (let secondCharCode = 'B'.charCodeAt(0); secondCharCode < 'D'.charCodeAt(0); secondCharCode++) {
    //   const secondChar = String.fromCharCode(secondCharCode);
    //   const col = 'A' + secondChar + '4';
    //   worksheet.getCell(col).fill = {
    //     type: 'pattern',
    //     pattern: 'solid',
    //     fgColor: { argb: 'DDEBF7' },
    //   };
    // }

    // for (let secondCharCode = 'D'.charCodeAt(0); secondCharCode < 'O'.charCodeAt(0); secondCharCode++) {
    //   const secondChar = String.fromCharCode(secondCharCode);
    //   const col = 'A' + secondChar + '4';
    //   worksheet.getCell(col).fill = {
    //     type: 'pattern',
    //     pattern: 'solid',
    //     fgColor: { argb: 'BDD7EE' },
    //   };
    // }

    // for (let secondCharCode = 'O'.charCodeAt(0); secondCharCode < 'Z'.charCodeAt(0); secondCharCode++) {
    //   const secondChar = String.fromCharCode(secondCharCode);
    //   const col = 'A' + secondChar + '4';
    //   worksheet.getCell(col).fill = {
    //     type: 'pattern',
    //     pattern: 'solid',
    //     fgColor: { argb: 'C6E0B4' },
    //   };
    // }

    // worksheet.getCell('AZ4').fill = {
    //   type: 'pattern',
    //   pattern: 'solid',
    //   fgColor: { argb: 'D9E1F2' },
    // };

    // //B Region
    // for (let secondCharCode = 'A'.charCodeAt(0); secondCharCode <= 'D'.charCodeAt(0); secondCharCode++) {
    //   const secondChar = String.fromCharCode(secondCharCode);
    //   const col = 'B' + secondChar + '4';
    //   worksheet.getCell(col).fill = {
    //     type: 'pattern',
    //     pattern: 'solid',
    //     fgColor: { argb: 'D9E1F2' },
    //   };
    // }

    const sections = [
      { range: ['A3', 'Y3'], label: 'Personal', color: 'E2EFDA' },
      { range: ['Z3', 'AB3'], label: 'Product Purchase', color: 'D9E1F2' },
      { range: ['AC3', 'AD3'], label: 'SOP Subscription', color: 'DDEBF7' },
      { range: ['AE3', 'AO3'], label: 'Pre-assessment', color: 'BDD7EE' },
      { range: ['AP3', 'AZ3'], label: 'Post-assessment', color: 'C6E0B4' },
      { range: ['BA3', 'BE3'], label: 'Results', color: 'D9E1F2' },
    ];

    sections.forEach(({ range, label, color }) => {
      // Merge cells (e.g., 'Z3:AB3')
      const [start, end] = range;
      worksheet.mergeCells(`${start}:${end}`);

      // Set merged header cell styles
      const headerCell = worksheet.getCell(start);
      headerCell.value = label;
      headerCell.alignment = { horizontal: 'center', vertical: 'middle' };
      headerCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: color },
      };

      // Fill cells in row 4 under the merged range
      const startCol = worksheet.getColumn(start.replace(/[0-9]/g, '')).number;
      const endCol = worksheet.getColumn(end.replace(/[0-9]/g, '')).number;

      for (let col = startCol; col <= endCol; col++) {
        const cell = worksheet.getCell(4, col); // Row 4
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: color },
        };
      }
    });

    for (let i = 0; i < result?.length; i++) {
      const item = result[i];
      const userId = item.userId;
      const teamId = item.teamId;
      const transactionItem = mappedResult[teamId][userId];
      const isIndividualCompleted =
        transactionItem.purchase?.orderStatus === 1 &&
        transactionItem.weightOut?.weightedDate != null &&
        transactionItem.weightOut?.weight != null &&
        transactionItem.weightIn?.weightedDate != null;
      let row = {};

      row = {
        campaignId: item.campaignId,
        teamName: item.teamName,
        teamDescription: item.teamDescription,
        teamStartDate: item.teamStartDate ? moment(item.teamStartDate).format('DD-MM-YYYY') : null,
        teamEndDate: item.teamEndDate ? moment(item.teamEndDate).format('DD-MM-YYYY') : null,
        teamStatus: item.teamStatus,
        coachId: item.coachID,
        isIndividualTeam: item.isIndividualTeam === 1 ? 'Y' : 'N',
        currentTeamSize: item.currentTeamSize,
        //isTeamLeader: item.teamLeaderUserId === item.userId ? 'Y' : 'N',
        aboId: item.loginId,
        name: item.name,
        phone: item.phone,
        email: item.email,
        address: item.address,
        race: item.race,
        gender: item.gender,
        currentPinLevel: item.pinLevel,
        highestPinAchieved: item.highestPinAchieved,
        baLoaName: item.baLoaName,
        memberType: item.memberType,
        isNewAbo: item.isNewAbo === 1 ? 'Y' : 'N',
        occupation: item.occupation,
        incomeRangePerMonth: item.incomeRangePerMonth,
        // isInvisible: item.memberType === 'INDIVIDUAL_INVISIBLE' ? 'Y' : 'N',
        remark: item.remark,
        enrollmentStatus: item.enrollmentStatus,
        orderNumber: transactionItem?.purchase?.orderNumber,
        productName: transactionItem?.purchase?.orderName,
        orderQualified: transactionItem?.purchase?.orderStatus === 1 ? 'Y' : 'N',
        // orderPoint: transactionItem?.purchasePoint,
        // orderAdjustedPoint: transactionItem?.purchaseAdjPoint,
        alpOrderNumber: transactionItem?.subscribe?.orderNumber,
        alpOrderQualified: transactionItem?.subscribe?.orderStatus === 1 ? 'Y' : 'N',
        // alp: transactionItem?.subscribe?.orderNumber,
        // alpPoint: transactionItem?.subscribePoint,
        // alpAdjustedPoint: transactionItem?.subscribeAdjPoint,
        weightInDate: transactionItem?.weightIn?.weightedDate
          ? moment(transactionItem.weightIn.weightedDate).format('DD-MM-YYYY')
          : '',
        weightInMachine: transactionItem.weightIn?.machine,
        weightInHeight: transactionItem.weightIn?.weightHeight
          ? getRoundedNumber(decryptString(transactionItem.weightIn?.weightHeight))
          : '',
        weightInWeight: transactionItem.weightIn?.weight
          ? getRoundedNumber(decryptString(transactionItem.weightIn?.weight))
          : '',
        weightInMuscleMass: transactionItem.weightIn?.muscleKilogram
          ? getRoundedNumber(decryptString(transactionItem.weightIn?.muscleKilogram))
          : '',
        weightInMusclePercentage: transactionItem.weightIn?.musclePercentage
          ? getRoundedNumber(decryptString(transactionItem.weightIn?.musclePercentage))
          : '',
        weightInBodyFat: transactionItem.weightIn?.bodyFat
          ? getRoundedNumber(decryptString(transactionItem.weightIn?.bodyFat))
          : '',
        weightInAcknowledgeBy: transactionItem.weightIn?.acknowledgeByName,
        weightInAcknowledgeDate: transactionItem.weightIn?.acknowledgeDateTime
          ? moment(transactionItem.weightIn.acknowledgeDateTime).format('DD-MM-YYYY')
          : '',
        weightInPhotoFront: item.weightInPhotoFront ? 'Y' : 'N',
        weightInPhotoBack: item.weightInPhotoBack ? 'Y' : 'N',
        weightOutDate: transactionItem.weightOut?.weightedDate
          ? moment(transactionItem.weightOut.weightedDate).format('DD-MM-YYYY')
          : '',
        weightOutMachine: transactionItem.weightOut?.machine,
        weightOutHeight: transactionItem.weightOut?.weightHeight
          ? getRoundedNumber(decryptString(transactionItem.weightOut?.weightHeight))
          : '',
        weightOutWeight: transactionItem.weightOut?.weight
          ? getRoundedNumber(decryptString(transactionItem.weightOut?.weight))
          : '',
        weightOutMuscleMass: transactionItem.weightOut?.muscleKilogram
          ? getRoundedNumber(decryptString(transactionItem.weightOut?.muscleKilogram))
          : '',
        weightOutMusclePercentage: transactionItem.weightOut?.musclePercentage
          ? getRoundedNumber(decryptString(transactionItem.weightOut?.musclePercentage))
          : null,
        weightOutBodyFat: transactionItem.weightOut?.bodyFat
          ? getRoundedNumber(decryptString(transactionItem.weightOut?.bodyFat))
          : '',
        weightOutAcknowledgeBy: transactionItem.weightOut?.acknowledgeByName,
        weightOutAcknowledgeDate: transactionItem.weightOut?.acknowledgeDateTime
          ? moment(transactionItem.weightOut.acknowledgeDateTime).format('DD-MM-YYYY')
          : '',
        weightOutPhotoFront: item.weightOutPhotoFront ? 'Y' : 'N',
        weightOutPhotoBack: item.weightOutPhotoBack ? 'Y' : 'N',
        // teamWeightDiff: mappedResult[teamId].teamWeightDiff,
        // participantWeightDiff: transactionItem.weightOutDiffValues?.weightDiff,
        // teamBodyFatDiff: mappedResult[teamId].teamBodyFatDiff,
        participantBodyFatDiff: transactionItem.weightOutDiffValues?.bodyFatDiff,
        // teamMuscleMassDiff: mappedResult[teamId].teamMuscleMassDiff,
        participantMuscleMassDiff: transactionItem.weightOutDiffValues?.muscleKilogramDiff,
        participantMusclePercentageDiff: transactionItem.weightOutDiffValues?.musclePercentageDiff,
        // winBodyWeight:
        //   -transactionItem.weightOutDiffValues?.weightPercentageDiff >= parseFloat(criteria.indiWeightToLoss)
        //     ? 'Y'
        //     : 'N',
        // winBodyFat:
        //   -transactionItem.weightOutDiffValues?.bodyFatDiff >= parseFloat(criteria.indiBodyFatToLossIndi) ? 'Y' : 'N',
        // winMuscleMass:
        //   transactionItem.weightOutDiffValues?.musclePercentageDiff >= parseFloat(criteria.indiMuscleToGainIndi)
        //     ? 'Y'
        //     : 'N',
        // groupWinningPrize: teamCriteria[teamId].result ? 'Y' : 'N',
        // coachPoint: transactionItem?.winningPoint,
        // coachAdjustedPoint: transactionItem?.winningAdjPoint,
        completedJourney: isIndividualCompleted ? 'Y' : 'N',
        achievedIndividualCriteria: transactionItem.weightOut?.isIndividualAchieved ? 'Y' : 'N',
      };

      for (let i = 0; i < transactionItem?.rewards.length; i++) {
        let keyVariable = 'reward' + (i + 1).toString();
        let valueArray = transactionItem?.rewards[i];
        let data = { [keyVariable]: valueArray };
        row = { ...row, ...data };
      }

      worksheet.addRow(row);
    }

    return workbook;
  },
  async coachGroupReport(name: string, campaignDetail: DynamicObjectInterface) {
    const workbook = new excel.Workbook();

    const worksheet = workbook.addWorksheet(`Coach List`);

    worksheet.mergeCells('A1', 'C1');
    worksheet.getCell('A1').value = 'Report - Coach List';
    worksheet.getCell('A1').font = { bold: true };

    worksheet.getCell('A2').value = 'Campaign';
    worksheet.getCell('A2').font = { bold: true };

    // Column headers
    let maxRewardsLength = 0;
    let headers = [
      'No.',
      'Coach ID',
      'Name',
      'ABO ID',
      'Current Pin',
      'Direct Platinum Name',
      'Direct Platinum ABO ID',
      'Direct Diamond Name',
      'Direct Diamond ABO ID',
      'Period',
      'Number of Teams - Total',
      'Number of Participants - Completed Registration',
      'Number of Participants - Completed Pre-assessment',
      'Number of Participants - Completed Post-assessment',
      //'Number of Participants - Completed Journey',
      'Number of Participants - Achieved Individual Criteria',
      'Number of Participants - Subscribed to SOP',
      'Number of New ABOs',
    ];

    let columns = [
      { key: 'no', width: 15 },
      { key: 'coachId', width: 20 },
      { key: 'name', width: 25 },
      { key: 'aboId', width: 20 },
      { key: 'pin', width: 25 },
      { key: 'directLeaderName', width: 50 },
      { key: 'directLeaderAboId', width: 40 },
      { key: 'directDiamondName', width: 50 },
      { key: 'directDiamondAboId', width: 40 },
      { key: 'period', width: 30 },
      { key: 'totalTeams', width: 25 },
      { key: 'totalRegister', width: 25 },
      { key: 'numOfWeightIn', width: 25 },
      { key: 'numOfWeightOut', width: 30 },
      //{ key: 'numOfCompletedJourney', width: 25 },
      { key: 'numOfAchievedIndividualCriteria', width: 30 },
      { key: 'numOfSubscribedALP', width: 30 },
      { key: 'numOfNewAbo', width: 30 },
    ];

    let result = await axiosPost(`report/?name=${name}`, { campaignId: campaignDetail.campaignId });

    if (result.status === 200) {
      let listRow: DynamicObjectInterface[] = [];
      result.data.data.forEach(function (item: DynamicObjectInterface, index: string) {
        const rewards = item.rewards.map(
          (reward: DynamicObjectInterface) =>
            `${reward.rewardName} ${
              reward.redeemDate ? '(' + moment(reward.redeemDate).format('DD-MM-YYYY') + ')' : ''
            }`,
        );

        if (rewards != null && rewards && rewards.length > maxRewardsLength) {
          maxRewardsLength = rewards.length;
        }

        let row = {
          no: index + 1,
          coachId: item.coachId,
          name: item.name,
          aboId: item.aboId,
          pin: item.pin,
          directLeaderName: item.directLeaderName,
          directLeaderAboId: item.directLeader,
          directDiamondName: item.directDiamondName,
          directDiamondAboId: item.directDiamondAboId,
          period: `${moment(item.enrollmentDate).format('MMM YYYY')} - ${moment(item.endDate).format('MMM YYYY')}`,
          totalTeams: item.totalTeams || 0,
          totalRegister: item.totalRegister || 0,
          numOfWeightIn: item.numOfWeightIn || 0,
          numOfWeightOut: item.numOfWeightOut || 0,
          //numOfCompletedJourney: item.numOfCompletedJourney,
          numOfAchievedIndividualCriteria: item.numOfAchievedIndividualCriteria || 0,
          numOfSubscribedALP: item.numOfSubscribedALP || 0,
          numOfNewAbo: item.numOfTotalNewAbo || 0,
          // wellnessAdventurePoints: item?.cumulativePoint
          //   ? parseFloat(
          //       (parseFloat(item?.cumulativePoint?.point) + parseFloat(item?.cumulativePoint?.pointAdj)).toFixed(1),
          //     )
          //   : 0,
          // personalSponsoredNewAbo: item?.newAboCount
          //   ? parseFloat((parseFloat(item?.newAboCount?.point) + parseFloat(item?.newAboCount?.pointAdj)).toFixed(1))
          //   : 0,
          // groupSponsoredNewAbo: item?.newGroupAboCount
          //   ? parseFloat(
          //       (parseFloat(item?.newGroupAboCount?.point) + parseFloat(item?.newGroupAboCount?.pointAdj)).toFixed(1),
          //     )
          //   : 0,
        };

        for (let i = 0; i < rewards.length; i++) {
          let keyVariable = 'reward' + (i + 1).toString();
          let valueArray = rewards[i];
          let data = { [keyVariable]: valueArray };
          row = { ...row, ...data };
        }
        listRow.push(row);
      });

      if (maxRewardsLength > 0) worksheet.mergeCells(3, 18, 3, 18 + (maxRewardsLength - 1));
      const rowRewards = worksheet.getRow(3);
      rowRewards.getCell(18).value = 'Rewards';
      rowRewards.getCell(18).alignment = { horizontal: 'center', vertical: 'middle' };
      rowRewards.getCell(18).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FCE4D6' },
      };

      for (let i = 1; i <= maxRewardsLength; i++) {
        headers.push('Reward ' + i.toString());
        columns.push({ key: 'reward' + i, width: 30 });
      }

      if (maxRewardsLength <= 0) {
        headers.push('Reward ' + 1);
        columns.push({ key: 'reward' + 1, width: 30 });
      }

      worksheet.getRow(4).values = headers;
      worksheet.columns = columns;
      worksheet.getRow(4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FCE4D6' } };
      worksheet.getRow(4).alignment = { vertical: 'bottom', horizontal: 'center' };

      worksheet.mergeCells('B2', 'D2');
      worksheet.getCell('B2').value = `${campaignDetail.campaignId} - ${campaignDetail.campaignName}`;
      worksheet.getCell('B2').font = { color: { argb: 'FF0000' } };

      listRow.forEach(row => {
        worksheet.addRow(row);
      });
    }
    return workbook;
  },
  async coachIndividualReport(name: string, campaignDetail: DynamicObjectInterface) {
    const workbook = new excel.Workbook();

    const worksheet = workbook.addWorksheet('Report - Coach Individual');

    let data = await axiosPost(`report/?name=${name}`, { campaignId: campaignDetail.campaignId });
    let result: DynamicObjectInterface[] = Object.values(data?.data?.data?.data);
    let criteria = data?.data?.data?.criteria;

    const teamsByCoach: DynamicObjectInterface[] = [];
    result.forEach((team: DynamicObjectInterface) => {
      const coachId = team.coachID;
      if (!teamsByCoach[coachId]) {
        teamsByCoach[coachId] = [team];
      } else {
        teamsByCoach[coachId].push(team);
      }
    });

    const mappedTotalPoint = [];

    for (let i = 0; i < result.length; i++) {
      if (!mappedTotalPoint[result?.[i]?.coachID]) {
        mappedTotalPoint[result?.[i]?.coachID] = {
          purchaseTotalPoint: 0,
          purchaseAdjTotalPoint: 0,
          subscribeTotalPoint: 0,
          subscribeAdjTotalPoint: 0,
          winningTotalPoint: 0,
          winningAdjTotalPoint: 0,
          coachTotalPoint: 0,
        };
      }

      if (result?.[i]?.enrollmentStatus === 'ACTIVE') {
        mappedTotalPoint[result?.[i]?.coachID].purchaseTotalPoint += result?.[i]?.point?.purchase ?? 0;
        mappedTotalPoint[result?.[i]?.coachID].purchaseAdjTotalPoint += result?.[i]?.point?.purchaseAdj ?? 0;
        mappedTotalPoint[result?.[i]?.coachID].subscribeTotalPoint += result?.[i]?.point?.subscribe ?? 0;
        mappedTotalPoint[result?.[i]?.coachID].subscribeAdjTotalPoint += result?.[i]?.point?.subscribeAdj ?? 0;
        mappedTotalPoint[result?.[i]?.coachID].winningTotalPoint += result?.[i]?.point?.winning ?? 0;
        mappedTotalPoint[result?.[i]?.coachID].winningAdjTotalPoint += result?.[i]?.point?.winningAdj ?? 0;

        mappedTotalPoint[result?.[i]?.coachID].coachTotalPoint =
          mappedTotalPoint[result?.[i]?.coachID].purchaseTotalPoint +
          mappedTotalPoint[result?.[i]?.coachID].purchaseAdjTotalPoint +
          mappedTotalPoint[result?.[i]?.coachID].subscribeTotalPoint +
          mappedTotalPoint[result?.[i]?.coachID].subscribeAdjTotalPoint +
          mappedTotalPoint[result?.[i]?.coachID].winningTotalPoint +
          mappedTotalPoint[result?.[i]?.coachID].winningAdjTotalPoint;
      }
    }

    const mappedResult: DynamicObjectInterface[] = [];

    for (let i = 0; i < result.length; i++) {
      if (!mappedResult[result?.[i]?.team?.teamId]) {
        mappedResult[result?.[i]?.team?.teamId] = {
          teamWeightDiff: 0,
          teamBodyFatDiff: 0,
          teamMuscleMassDiff: 0,
        };
      }

      if (result?.[i]?.enrollmentStatus === 'ACTIVE') {
        mappedResult[result?.[i]?.team.teamId].teamWeightDiff +=
          result?.[i]?.weightOutDiffValues?.weightPercentageDiff ?? 0;
        mappedResult[result?.[i]?.team.teamId].teamBodyFatDiff += result?.[i]?.weightOutDiffValues?.bodyFatDiff ?? 0;
        mappedResult[result?.[i]?.team.teamId].teamMuscleMassDiff +=
          result?.[i]?.weightOutDiffValues?.muscleKilogramDiff ?? 0;
      }
    }

    worksheet.getCell('A1').value = 'Report - Coach Individual';
    worksheet.getCell('A1').font = { bold: true };
    worksheet.getCell('A2').value = `Campaign - ${campaignDetail.campaignId} - ${campaignDetail.campaignName}`;
    worksheet.getCell('A2').font = { bold: true };
    worksheet.addRow([]);

    for (const coachId in teamsByCoach) {
      if (Object.prototype.hasOwnProperty.call(teamsByCoach, coachId)) {
        const teamData = teamsByCoach[coachId];

        const row1Data = [
          'CoachID',
          teamsByCoach[coachId][0].coachID,
          '',
          '',
          'Total Point',
          teamsByCoach[coachId][0].cumulativePoint.point + teamsByCoach[coachId][0].cumulativePoint.pointAdj,
          '',
          '',
          'Reward',
          teamsByCoach[coachId][0].rewards
            .map(
              (reward: DynamicObjectInterface) =>
                `${reward.rewardName} ${
                  reward.redeemDate ? `(${moment(reward.redeemDate).format('DD-MM-YYYY')})` : ''
                }`,
            )
            .join(', '),
        ];
        const addedRow = worksheet.addRow(row1Data);
        const cellIndicesToColor = [1, 5, 9];
        cellIndicesToColor.forEach(index => {
          addedRow.getCell(index).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E2EFDA' },
          };
        });

        const row2Data = [
          'Coach ABO ID',
          `${teamsByCoach[coachId][0].coachAboId} - ${teamsByCoach[coachId][0].coachName}`,
          '',
          '',
          'Personal ABO Sponsored',
          teamsByCoach[coachId][0].newAboCount.point + teamsByCoach[coachId][0].newAboCount.pointAdj,
        ];
        const addedRow2 = worksheet.addRow(row2Data);
        const cellIndicesToColor2 = [1, 5];
        cellIndicesToColor2.forEach(index => {
          addedRow2.getCell(index).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E2EFDA' },
          };
        });

        const row3Data = [
          'Leader ABO ID',
          `${teamsByCoach[coachId][0].coachDirectLeaderAboId} - ${teamsByCoach[coachId][0].coachDirectLeaderName}`,
          '',
          '',
          'Group ABO Sponsored',
          teamsByCoach[coachId][0].newGroupAboCount.point + teamsByCoach[coachId][0].newGroupAboCount.pointAdj,
        ];
        const addedRow3 = worksheet.addRow(row3Data);
        const cellIndicesToColor3 = [1, 5];
        cellIndicesToColor3.forEach(index => {
          addedRow3.getCell(index).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E2EFDA' },
          };
        });
        worksheet.addRow([]);
        worksheet.addRow([]);

        worksheet.mergeCells(`G${worksheet?.lastRow?.number}:J${worksheet?.lastRow?.number}`);
        const orderAllocationCell = worksheet.getCell(`G${worksheet?.lastRow?.number}`);
        orderAllocationCell.value = 'Order Allocation';
        orderAllocationCell.alignment = { horizontal: 'center', vertical: 'middle' };
        orderAllocationCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'ccddff' },
        };
        orderAllocationCell.font = { size: 15 };

        worksheet.mergeCells(`K${worksheet?.lastRow?.number}:M${worksheet?.lastRow?.number}`);
        const alpCell = worksheet.getCell(`K${worksheet?.lastRow?.number}`);
        alpCell.value = 'SOP Subscription';
        alpCell.alignment = { horizontal: 'center', vertical: 'middle' };
        alpCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'ccf7ff' },
        };
        alpCell.font = { size: 15 };

        worksheet.mergeCells(`N${worksheet?.lastRow?.number}:S${worksheet?.lastRow?.number}`);
        const resultCell = worksheet.getCell(`N${worksheet?.lastRow?.number}`);
        resultCell.value = 'Results';
        resultCell.alignment = { horizontal: 'center', vertical: 'middle' };
        resultCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'dbccff' },
        };
        resultCell.font = { size: 15 };

        worksheet.mergeCells(`T${worksheet?.lastRow?.number}:X${worksheet?.lastRow?.number}`);
        const winningCell = worksheet.getCell(`T${worksheet?.lastRow?.number}`);
        winningCell.value = 'Winning Criteria';
        winningCell.alignment = { horizontal: 'center', vertical: 'middle' };
        winningCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'ebf218' },
        };
        winningCell.font = { size: 15 };

        const headers = [
          'Team Name',
          'Team Status',
          'Participant ABO ID',
          'Participant Status',
          'Weigh In Date',
          'Weigh Out Date',
          'Order Number',
          'Product Name',
          'Point',
          'Adjusted Point',
          'SOP Subscription',
          'Point',
          'Adjusted Point',
          'Team Weight diff (%)',
          'Participant Weight diff (kg)',
          'Team Body Fat diff (%)',
          'Participant Body Fat diff (%)',
          'Team Muscle Mass diff (%)',
          'Participant Muscle Mass diff (kg)',
          'Body Weight',
          'Body Fat',
          'Muscle Mass',
          'Point',
          'Adjusted Point',
          'Total Point',
        ];
        worksheet.addRow(headers);

        for (let colCode = 'G'.charCodeAt(0); colCode <= 'J'.charCodeAt(0); colCode++) {
          const col = String.fromCharCode(colCode);
          const cellAddress = `${col}${worksheet?.lastRow?.number}`;
          worksheet.getCell(cellAddress).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'ccddff' },
          };
        }

        for (let colCode = 'K'.charCodeAt(0); colCode <= 'M'.charCodeAt(0); colCode++) {
          const col = String.fromCharCode(colCode);
          const cellAddress = `${col}${worksheet?.lastRow?.number}`;
          worksheet.getCell(cellAddress).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'ccf7ff' },
          };
        }

        for (let colCode = 'N'.charCodeAt(0); colCode <= 'S'.charCodeAt(0); colCode++) {
          const col = String.fromCharCode(colCode);
          const cellAddress = `${col}${worksheet?.lastRow?.number}`;
          worksheet.getCell(cellAddress).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'dbccff' },
          };
        }

        for (let colCode = 'T'.charCodeAt(0); colCode <= 'X'.charCodeAt(0); colCode++) {
          const col = String.fromCharCode(colCode);
          const cellAddress = `${col}${worksheet?.lastRow?.number}`;
          worksheet.getCell(cellAddress).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'ebf218' },
          };
        }

        for (let colCode = 'Y'.charCodeAt(0); colCode <= 'Y'.charCodeAt(0); colCode++) {
          const col = String.fromCharCode(colCode);
          const cellAddress = `${col}${worksheet?.lastRow?.number}`;
          worksheet.getCell(cellAddress).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E2EFDA' },
          };
        }

        teamData.forEach((row: DynamicObjectInterface) => {
          const rowData = [
            row.team.teamName,
            row.team.teamStatus,
            row.loginId,
            row.enrollmentStatus,
            row.weightIn.weightedDate,
            row.weightOut.weightedDate,
            row.purchase.map((purchase: DynamicObjectInterface) => purchase.orderNumber).toString(),
            row.purchase.map((purchase: DynamicObjectInterface) => purchase.orderName).toString(),
            row.point.purchase,
            row.point.purchaseAdj,
            row.subscribe.map((subscribe: DynamicObjectInterface) => subscribe.orderNumber).toString(),
            row.point.subscribe,
            row.point.subscribeAdj,
            mappedResult[row.team.teamId].teamWeightDiff ? mappedResult[row.team.teamId].teamWeightDiff.toFixed(1) : 0,
            row.weightOutDiffValues?.weightDiff ? row.weightOutDiffValues.weightDiff.toFixed(1) : 0,
            mappedResult[row.team.teamId]?.teamBodyFatDiff
              ? mappedResult[row.team.teamId].teamBodyFatDiff.toFixed(1)
              : 0,
            row.weightOutDiffValues?.bodyFatPercentageDiff
              ? row.weightOutDiffValues.bodyFatPercentageDiff.toFixed(1)
              : 0,
            mappedResult[row.team.teamId]?.teamMuscleMassDiff
              ? mappedResult[row.team.teamId].teamMuscleMassDiff.toFixed(1)
              : 0,
            row.weightOutDiffValues?.muscleKilogramDiff ? row.weightOutDiffValues.muscleKilogramDiff.toFixed(1) : 0,
            -row.weightOutDiffValues?.weightDiff >= parseFloat(criteria.indiWeightToLoss) ? 'Y' : 'N',
            -row.weightOutDiffValues?.bodyFatDiff >= parseFloat(criteria.indiBodyFatToLossIndi) ? 'Y' : 'N',
            row.weightOutDiffValues?.muscleKilogramDiff >= parseFloat(criteria.indiMuscleToGainIndi) ? 'Y' : 'N',
            row.point.winning,
            row.point.winningAdj,
            row.point.purchase +
              row.point.purchaseAdj +
              row.point.subscribe +
              row.point.subscribeAdj +
              row.point.winning +
              row.point.winningAdj,
          ];
          worksheet.addRow(rowData);
        });
        worksheet.addRow([]);
        worksheet.addRow([]);
      }
    }

    return workbook;
  },

  async inventoryReport(name: string, campaignDetail: DynamicObjectInterface) {
    const workbook = new excel.Workbook();
    const worksheet = workbook.addWorksheet(`Inventory List`);

    worksheet.mergeCells('A1', 'C1');
    worksheet.getCell('A1').value = 'Report - Inventory List';
    worksheet.getCell('A1').font = { bold: true };

    worksheet.getCell('A2').value = 'Campaign';
    worksheet.getCell('A2').font = { bold: true };
    worksheet.mergeCells('B2', 'D2');
    worksheet.getCell('B2').value = `${campaignDetail.campaignId} - ${campaignDetail.campaignName}`;
    worksheet.getCell('B2').font = { color: { argb: 'FF0000' } };

    const result = await axiosPost(`report/?name=${name}`, { campaignId: campaignDetail.campaignId });

    const headers = [
      'No.',
      'Campaign ID',
      'Location Code',
      'Location Name',
      'Reward Code',
      'Reward Name',
      'Available Stock',
      'Redeemed Stock',
      'Status',
    ];

    const columns = [
      { key: 'no', width: 15 },
      { key: 'campaignId', width: 20 },
      { key: 'locationCode', width: 20 },
      { key: 'locationName', width: 30 },
      { key: 'rewardCode', width: 25 },
      { key: 'rewardName', width: 30 },
      { key: 'availableStock', width: 20 },
      { key: 'redeemedStock', width: 20 },
      { key: 'status', width: 20 },
    ];

    const listRow: DynamicObjectInterface[] = [];

    if (result.status === 200) {
      result.data.data.forEach((item: DynamicObjectInterface, index: number) => {
        const row: DynamicObjectInterface = {
          no: index + 1,
          campaignId: item.campaignId,
          locationCode: item.locationCode,
          locationName: item.locationName,
          rewardCode: item.rewardCode,
          rewardName: item.rewardName,
          availableStock: item.availableStock,
          redeemedStock: item.redeemedStock,
          status: item.status,
        };

        listRow.push(row);
      });

      headers.forEach((header, index) => {
        worksheet.getRow(4).getCell(index + 1).value = header;
      });

      worksheet.columns = columns;
      worksheet.getRow(4).alignment = { vertical: 'bottom', horizontal: 'center' };

      for (let i = 1; i <= 9; i++) {
        worksheet.getRow(4).getCell(i).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'BDD7EE' },
        };
      }

      listRow.forEach(row => worksheet.addRow(row));
    }

    return workbook;
  },

  async promoRedemptionReport(name: string, campaignDetail: DynamicObjectInterface) {
    const workbook = new excel.Workbook();
    const worksheet = workbook.addWorksheet(`Redemption List`);

    worksheet.mergeCells('A1', 'C1');
    worksheet.getCell('A1').value = 'Report - Promotion Redemption List';
    worksheet.getCell('A1').font = { bold: true };

    worksheet.getCell('A2').value = 'Campaign';
    worksheet.getCell('A2').font = { bold: true };
    worksheet.mergeCells('B2', 'D2');
    worksheet.getCell('B2').value = `${campaignDetail.campaignId} - ${campaignDetail.campaignName}`;
    worksheet.getCell('B2').font = { color: { argb: 'FF0000' } };

    const result = await axiosPost(`report/?name=${name}`, { campaignId: campaignDetail.campaignId });

    if (result.status === 200) {
      let isChallenge = result.data.data[0].channel === 'CHALLENGE'

      let headers = [];
      let columns = [];
      let listRow: DynamicObjectInterface[] = [];

      if (isChallenge){
        headers = [
          'No.',
          'Campaign ID',
          'Reference ID',
          'ABO ID',
          'Name',
          'Location Code',
          'Location Name',
          'Channel',
          "Entitled Ticket",
          "Reward Code",
          "Reward Name",
          "Reward Status",
          "Claimed By",
          "Claimed Date"
        ];

        columns = [
          { key: 'no', width: 15 },
          { key: 'campaignId', width: 20 },
          { key: 'orderNumber', width: 25 },
          { key: 'orderAboId', width: 20 },
          { key: 'orderAboName', width: 35 },
          { key: 'locationCode', width: 15 },
          { key: 'locationName', width: 30 },
          { key: 'channel', width: 15 },
          { key: 'chances', width: 15 },
          { key: 'rewardCode', width: 20 },
          { key: 'rewardName', width: 20 },
          { key: 'status', width: 15 },
          { key: 'player', width: 20 },
          { key: 'createdDate', width: 20 },
        ];

        result.data.data.forEach((item: DynamicObjectInterface, index: number) => {
          const row: DynamicObjectInterface = {
            no: index + 1,
            campaignId: item.campaignId,
            name: item.orderNumber,
            orderNumber: item.orderNumber,
            orderAboId: item.orderAboId,
            orderAboName: item.orderAboName,
            locationCode: item.locationCode,
            locationName: item.locationName,
            channel: item.channel,
            chances: item.chances,
            rewardCode: item.rewardCode,
            rewardName: item.rewardName,
            status: item.status,
            player: item.player,
            createdDate: convertLocalDate(item.createdDate,'DD-MM-YYYY HH:mm:ss'),
          };

          listRow.push(row);
        });

      }else{

        headers = [
          'No.',
          'Campaign ID',
          'Order Number',
          'Ordering ABO ID',
          'Ordering Name',
          'OrderFor ABO ID',
          'OrderFor Name',
          'Location Code',
          'Location Name',
          'Channel',
          "Entitled Ticket",
          "Reward Code",
          "Reward Name",
          "Reward Status",
          "Claimed By",
          "Claimed Date"  
        ];

        columns = [
          { key: 'no', width: 15 },
          { key: 'campaignId', width: 20 },
          { key: 'orderNumber', width: 25 },
          { key: 'orderAboId', width: 20 },
          { key: 'orderAboName', width: 35 },
          { key: 'volumeAboId', width: 20 },
          { key: 'volumeAboName', width: 35 },
          { key: 'locationCode', width: 15 },
          { key: 'locationName', width: 30 },
          { key: 'channel', width: 15 },
          { key: 'chances', width: 15 },
          { key: 'rewardCode', width: 20 },
          { key: 'rewardName', width: 20 },
          { key: 'status', width: 15 },
          { key: 'player', width: 20 },
          { key: 'createdDate', width: 20 },
        ];

        result.data.data.forEach((item: DynamicObjectInterface, index: number) => {
          const row: DynamicObjectInterface = {
            no: index + 1,
            campaignId: item.campaignId,
            name: item.orderNumber,
            orderNumber: item.orderNumber,
            orderAboId: item.orderAboId,
            orderAboName: item.orderAboName,
            volumeAboId: item.volumeAboId,
            volumeAboName: item.volumeAboName,
            locationCode: item.locationCode,
            locationName: item.locationName,
            channel: item.channel,
            chances: item.chances,
            rewardCode: item.rewardCode,
            rewardName: item.rewardName,
            status: item.status,
            player: item.player,
            createdDate: convertLocalDate(item.createdDate,'DD-MM-YYYY HH:mm:ss'),
          };

          listRow.push(row);
        });

      }

      headers.forEach((header, index) => {
        worksheet.getRow(4).getCell(index + 1).value = header;
      });

      worksheet.columns = columns;
      worksheet.getRow(4).alignment = { vertical: 'bottom', horizontal: 'center' };

      for (let i = 1; i <= 16; i++) {
        worksheet.getRow(4).getCell(i).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'BDD7EE' },
        };
      }

      listRow.forEach(row => worksheet.addRow(row));
    }
    return workbook;
  },
};

export default generateReport;
